import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { countryName, sortedCountries } from '@/lib/countries';

// A searchable country picker. ~250 entries is too many for a plain Select, so this is the shadcn
// combobox pattern: a Popover holding a cmdk Command list, searchable by name in the UI language
// and by code ("ae", "egy" and "مصر" all find their country).

// Radix Select rejects '' as an item value, and so would anything reading this value back as a
// country, so "no country" travels as this sentinel inside the list and as '' outside it.
const NONE = '__none__';

interface CountrySelectProps {
  id?: string;
  /** ISO alpha-2 code, or '' for no country. */
  value: string;
  onChange: (code: string) => void;
  /** The option meaning "no country": "All countries" in a filter, "No country" on a form. */
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
}

export function CountrySelect({
  id,
  value,
  onChange,
  emptyLabel = 'No country',
  className,
  disabled,
}: CountrySelectProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [open, setOpen] = useState(false);

  const countries = useMemo(() => sortedCountries(lang), [lang]);
  const pinned = countries.filter((c) => c.pinned);
  const rest = countries.filter((c) => !c.pinned);

  const select = (code: string) => {
    onChange(code === NONE ? '' : code);
    setOpen(false);
  };

  const item = (code: string, name: string) => (
    <CommandItem key={code} value={code} keywords={[name, code]} onSelect={select}>
      <Check className={cn('mr-2 h-4 w-4', value === code ? 'opacity-100' : 'opacity-0')} />
      <span className="truncate">{name}</span>
      <span className="ml-auto pl-2 text-xs text-muted-foreground">{code}</span>
    </CommandItem>
  );

  return (
    // `modal`, because this opens inside the contact Dialog: without it the Dialog's scroll lock
    // swallows wheel events and the list cannot be scrolled with a mouse.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{value ? countryName(value, lang) : emptyLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value={NONE} keywords={[emptyLabel]} onSelect={select}>
                <Check className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                {emptyLabel}
              </CommandItem>
              {pinned.map((c) => item(c.code, c.name))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              {rest.map((c) => item(c.code, c.name))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CountrySelect;
